import { useEffect, useRef, useState } from 'react'

import { useField } from '@unform/core'

import { FieldLabel, ToggleButton, ToggleGroup } from './styles'

type MultiSelectFieldProps = {
  name: string
  label: string
  allLabel: string
  required?: boolean
  options: { value: string; label: string }[]
  onChange?(value: string[]): void
}

/**
 * Multi-select with a bidirectional "Select all" control (FR-017g):
 * toggling it selects/clears every option; unchecking a single option
 * clears "Select all" without touching the rest; "Select all" reflects
 * checked exactly when every option is selected, however that state was
 * reached. "Select all" is never itself a stored value — the field's value
 * is always the real option list (full or partial), per FR-017g.
 */
const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  name,
  label,
  allLabel,
  required,
  options,
  onChange
}) => {
  const { fieldName, registerField, defaultValue } = useField(name)

  const [selected, setSelected] = useState<string[]>(defaultValue ?? [])

  const selectedRef = useRef(selected)
  selectedRef.current = selected

  const emitChange = (next: string[]) => {
    selectedRef.current = next
    setSelected(next)
    onChange?.(next)
  }

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: selectedRef,
      getValue: ref => ref.current,
      setValue: (ref, value: string[]) => {
        const next = value ?? []
        ref.current = next
        setSelected(next)
        onChange?.(next)
      },
      clearValue: ref => {
        ref.current = []
        setSelected([])
        onChange?.([])
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldName, registerField])

  const toggleOption = (value: string) => {
    emitChange(
      selectedRef.current.includes(value)
        ? selectedRef.current.filter(item => item !== value)
        : [...selectedRef.current, value]
    )
  }

  const allSelected = options.length > 0 && selected.length === options.length

  const toggleAll = () => {
    emitChange(allSelected ? [] : options.map(option => option.value))
  }

  return (
    <div>
      <FieldLabel>
        {label}
        {required && <span className="required"> *</span>}
      </FieldLabel>

      <ToggleGroup role="group" aria-label={label}>
        <ToggleButton
          type="button"
          data-selected={allSelected}
          aria-pressed={allSelected}
          onClick={toggleAll}
        >
          {allLabel}
        </ToggleButton>

        {options.map(option => (
          <ToggleButton
            key={option.value}
            type="button"
            data-selected={selected.includes(option.value)}
            aria-pressed={selected.includes(option.value)}
            onClick={() => toggleOption(option.value)}
          >
            {option.label}
          </ToggleButton>
        ))}
      </ToggleGroup>
    </div>
  )
}

export default MultiSelectField
